import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  NavLink,
  Button,
  Spinner,
  SpanWithData,
  CardTable as EmployerContactsTable,
  EmployerContactAdd
} from '../'

import {
  PageNavigationControl,
  readablePhoneNumber,
  getPageCursor
} from '../../utils'

import {
  DEFAULT_RESULTS_PER_PAGE
} from '../../constants'

export class EmployerContacts extends React.Component {
  static propTypes = {
    employerId: PropTypes.string.isRequired,
    contacts: PropTypes.object,
    title: PropTypes.string,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  state = {
    addContact: false,
    pageNav: new PageNavigationControl()
  }

  generateTableDataRows = (dataRows) => {
    let rows = dataRows.map((row, i) => (
      <EmployerContactTableDataRow data={row} key={i}/>
    ))

    return rows
  }

  toggleContactAddition = () => this.setState(prevState => (
    this.setState({ addContact: !prevState.addContact })
  ))

  handleEmployerContactSaved = () => {
    this.toggleContactAddition()
    this.props.refetch()
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    } else {
      let rows = this.generateTableDataRows(this.props.contacts.edges)
      let pageNav = this.state.pageNav

      return (
        <div className={this.props.className}>
          {this.state.addContact
            ? <EmployerContactAdd
                employerId={this.props.employer}
                onCancelClick={this.toggleContactAddition}
                onSaved={this.handleEmployerContactSaved}
              />
            : <EmployerContactsTable
                heading={this.props.title}
                headingIcon={this.props.title ? 'groups' : null}
                columns={[
                  'Name',
                  'Position',
                  'Division',
                  'Role',
                  'Communication method',
                  'Contact detail'
                ]}
                tableDataRows={rows}
                nextPage={() => pageNav.nextPage(this.props.fetchNext, this.props.employerId, DEFAULT_RESULTS_PER_PAGE)}
                previousPage={() => pageNav.previousPage(this.props.fetchPrevious, this.props.employerId, DEFAULT_RESULTS_PER_PAGE)}
                onFirstPage={pageNav.firstPage}
                onLastPage={pageNav.lastPage}
                startCursor={this.props.contacts.pageInfo.startCursor}
                totalCount={this.props.contacts.totalCount}
                resultsPerPage={DEFAULT_RESULTS_PER_PAGE}
                onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, DEFAULT_RESULTS_PER_PAGE) })}
                actionButtons={[
                  <Button
                    type='brand'
                    onClick={this.toggleContactAddition}
                    minWidth={150}
                    key='add'>
                    Add contact
                  </Button>
                ]}
              />
          }
        </div>
      )
    }
  }
}

const getAlternateActiveAddress = (addresses) => {
  let activeAddress = addresses.find(
    (address) => (
      !address.node.inactive
      && !address.node.optedOut
      && address.node.default
    )
  )

  return activeAddress
    ? activeAddress.node
    : null
}

const getDefaultAddress = (addresses, defaultAddressType) => {
  let defaultAddress = addresses.find(
    (address) => (
      defaultAddressType === address.node.addressType
    )
  )

  return defaultAddress
    ? defaultAddress.node
    // if no address value exist that corresponds to specified default address
    // type, get an address value that's active
    : getAlternateActiveAddress(addresses)
}

const getReadableAddressValue = (addressValue, addressType) => {
  if (['MSISDN', 'LANDLINE', 'FAX'].indexOf(addressType) > -1) {
    return readablePhoneNumber(addressValue)
  } else {
    return addressValue
  }
}

const EmployerContactTableDataRow = (props) => {
  let row = props.data.node
  let defaultAddress = getDefaultAddress(row.identity.addresses.edges, row.identity.defaultAddressType)

  return (
    <tr className='slds-hint-parent'>
      <th scope='row'>
        <div className='slds-truncate' title={row.name}>
          <NavLink to={'/os/identity/' + row.identity.id} >
            {row.identity.firstName+' '+row.identity.lastName}
          </NavLink>
        </div>
      </th>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.position}>
          {row.position}
        </div>
      </td>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.division}>
          {row.division ? row.division.name : '-'}
        </div>
      </td>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.role}>
          {row.role}
        </div>
      </td>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.identity.defaultAddressType}>
          <SpanWithData
            forField='IdentityDefaultAddressType'
            searchKey={defaultAddress ? defaultAddress.addressType : ''}
          />
        </div>
      </td>
      <td role='gridcell'>
        <div className='slds-truncate' title='Contact detail'>
          {defaultAddress
            ? getReadableAddressValue(defaultAddress.addressValue, defaultAddress.addressType)
            : ''
          }
        </div>
      </td>
    </tr>
  )
}

const getEmployerContacts = gql`
  query($id: UUID, $resultsPerPage: Int!, $cursor: String) {
    contacts(employer_Id: $id, orderBy: "identity__last_name", first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          ...Contact
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.contact}
`

const getPreviousEmployerContacts = gql`
  query($id: UUID, $resultsPerPage: Int!, $cursor: String) {
    contacts(employer_Id: $id, orderBy: "identity__last_name", last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          ...Contact
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.contact}
`

const withData = graphql(getEmployerContacts, {
  options: ({ employerId, resultsPerPage, cursor }) => ({
    variables: {
      id: employerId,
      resultsPerPage: DEFAULT_RESULTS_PER_PAGE,
      cursor
    }
  }),
  props: ({ data: { loading, refetch, contacts, fetchMore } }) => ({
    loading,
    refetch,
    contacts,
    fetchNext: (employerId, resultsPerPage) => {
      return fetchMore({
        query: getEmployerContacts,
        variables: {
          id: employerId,
          resultsPerPage,
          cursor: contacts.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.contacts.edges
          const pageInfo = fetchMoreResult.contacts.pageInfo
          const totalCount = fetchMoreResult.contacts.totalCount

          return {
            contacts: {
              __typename: previousResult.contacts.__typename,
              edges: [...newEdges],
              pageInfo,
              totalCount
            }
          }
        }
      })
    },
    fetchPrevious: (employerId, resultsPerPage) => {
      return fetchMore({
        query: getPreviousEmployerContacts,
        variables: {
          id: employerId,
          resultsPerPage,
          cursor: contacts.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.contacts.edges
          const pageInfo = fetchMoreResult.contacts.pageInfo
          const totalCount = fetchMoreResult.contacts.totalCount

          return {
            contacts: {
              __typename: previousResult.contacts.__typename,
              edges: [...newEdges],
              pageInfo,
              totalCount
            }
          }
        }
      })
    }
  })
})

export default withData(withRouter(EmployerContacts))



// WEBPACK FOOTER //
// ./src/components/employer-contacts/index.js